import { getHopinApiRoot } from '@hopin-team/config-hopin-constants';
import { packageJsonDetails } from '@hopin-team/package-details';

import packageJson from '../../package.json';

export interface ResponseError {
  type: 'ResponseError';
  error: Error;
  response?: Response;
  status?: number;
}

function toResponseError(
  error: Error,
  response?: Response,
  status?: number,
): ResponseError {
  return { type: 'ResponseError', response, error, status };
}

export const getUrlFromEnv = (route: string): string => {
  return `${getHopinApiRoot('v1')}/${route}`;
};

export type RequestInfoInit = [RequestInfo, RequestInit];

const getBody = async (response: Response) => {
  const body = await response.text();
  try {
    return JSON.parse(body);
  } catch (e) {
    return body;
  }
};

export function getLanguage() {
  const COOKIE_KEY = 'i18next=';
  const browserLanguage = window.navigator.languages?.join(',');
  const cookieLanguage = document.cookie
    ?.split('; ')
    .find(c => c.startsWith(COOKIE_KEY))
    ?.substr(COOKIE_KEY.length);

  return cookieLanguage || browserLanguage || 'en';
}

const fetchJSON = async (
  route: string,
  options: {
    [x: string]: any;
    body?:
      | Record<string, unknown>
      | Array<Record<string, unknown>>
      | Array<any>;
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    headers?: Record<string, unknown>;
  } = {},
) => {
  const { body, method = 'GET', headers = {}, ...rest } = options;
  const { version, name } = packageJsonDetails(packageJson);

  const url = route.match(/^(?:[a-z]+:)?\/\//i) ? route : getUrlFromEnv(route);

  try {
    const response = await fetch(url, {
      ...(method !== 'GET' && { body: JSON.stringify(body) }),
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': getLanguage(),
        'X-HOPIN-CLIENT-NAME': name,
        'X-HOPIN-CLIENT-VERSION': version,
        ...headers,
      },
      method,
      ...rest,
    });

    if (!response.ok) {
      const body = await getBody(response);

      throw toResponseError(
        new Error(`Request responded with status code ${response.status}`),
        { ...response, body },
        response.status,
      );
    }

    return await getBody(response);
  } catch (err) {
    // Handle pre-flight errors as 400 Bad request
    const anyErr = err as any;

    if (
      anyErr?.message === 'NetworkError when attempting to fetch resource.' || // Firefox
      anyErr?.message === 'Failed to fetch' || // Chrome
      anyErr?.message === 'Load failed' // Safari
    )
      throw toResponseError(anyErr, undefined, 400);

    throw err;
  }
};

export { fetchJSON };
